import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, TrendingUp, CheckCircle, Code, Scale, FileText } from 'lucide-react';

export const LawFirmPortfolio: React.FC = () => {
  const technologies = [
    'WordPress', 'PHP', 'MySQL', 'AJAX', 'SSL Security', 'GDPR Compliance'
  ];

  const features = [
    'Case Management Portal',
    'Client Communication System',
    'Document Sharing',
    'Consultation Booking',
    'Legal Resource Library',
    'Attorney Profiles',
    'Practice Area Pages',
    'Secure Client Portal'
  ];

  const results = [
    { metric: 'Lead Generation', value: '180%', description: 'Increase in qualified leads' },
    { metric: 'Client Satisfaction', value: '92%', description: 'Positive feedback on portal' },
    { metric: 'Case Efficiency', value: '45%', description: 'Improvement in case management' },
    { metric: 'Online Consultations', value: '65%', description: 'Of initial consultations online' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/portfolio" className="inline-flex items-center text-primary-100 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Portfolio
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <span className="px-3 py-1 bg-accent-500 text-white text-sm font-medium rounded-full">
                  Law Firm
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                Sterling & Associates Law
              </h1>
              <p className="text-xl text-primary-100 mb-8 leading-relaxed">
                A comprehensive legal practice platform with secure client portal, case management, 
                and consultation booking that increased lead generation by 180% and improved case efficiency by 45%.
              </p>
            </div>
            
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/5668772/pexels-photo-5668772.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                alt="Law Firm Office"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
              <div className="prose prose-lg max-w-none text-gray-600">
                <p className="mb-6">
                  Sterling & Associates Law needed a professional online presence that could handle 
                  client communications securely, showcase their expertise across multiple practice areas, 
                  and streamline their case management processes.
                </p>
                <p className="mb-6">
                  We developed a comprehensive WordPress solution with custom PHP development for secure 
                  client portals, case management tools, and consultation booking. The platform ensures 
                  attorney-client privilege protection while providing convenient access to case information 
                  and legal resources.
                </p>
                <p>
                  The implementation resulted in 180% increase in qualified leads, 92% client satisfaction 
                  with the portal system, and 45% improvement in case management efficiency.
                </p>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-6 w-6 text-primary-600 mr-2" />
                  Project Timeline
                </h3>
                <div className="space-y-3 text-gray-600">
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span className="font-medium">12 weeks</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Launch Date:</span>
                    <span className="font-medium">September 2023</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Practice Areas:</span>
                    <span className="font-medium">8 specialties</span>
                  </div>
                </div>
              </div>
              
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Code className="h-6 w-6 text-primary-600 mr-2" />
                  Technologies Used
                </h3>
                <div className="flex flex-wrap gap-2">
                  {technologies.map((tech, index) => (
                    <span key={index} className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Features Delivered</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional legal practice features designed to enhance client service and operational efficiency.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="card p-6 text-center">
                <CheckCircle className="h-8 w-8 text-accent-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900">{feature}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results & Impact */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Results & Impact</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Significant improvements in client acquisition and case management efficiency.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-primary-600 mb-2">{result.value}</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">{result.metric}</div>
                <div className="text-gray-600">{result.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Challenges & Solutions</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <Scale className="h-8 w-8 text-blue-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: Legal Compliance</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Ensuring all client communications and document sharing met strict attorney-client 
                privilege requirements and legal industry compliance standards.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Implemented end-to-end encryption, secure authentication, 
                audit trails, and GDPR compliance measures to protect sensitive legal information.
              </p>
            </div>
            
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <FileText className="h-8 w-8 text-green-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: Document Management</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Creating a secure system for sharing sensitive legal documents while maintaining 
                version control and access permissions.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Developed a secure document portal with role-based access, 
                version tracking, and automated notifications for document updates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonial */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="text-2xl font-medium mb-8">
            "The new platform has revolutionized how we serve our clients. The secure portal has improved 
            communication efficiency, and we've seen a dramatic increase in qualified leads."
          </blockquote>
          <div className="flex items-center justify-center">
            <img
              src="https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop"
              alt="James Sterling"
              className="w-16 h-16 rounded-full mr-4"
            />
            <div className="text-left">
              <div className="font-semibold">James Sterling</div>
              <div className="text-primary-200">Managing Partner, Sterling & Associates Law</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Need a Professional Legal Website?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let's discuss how we can create a secure, compliant platform that enhances your legal practice.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-started" className="btn-primary text-lg px-8 py-4">
              Start Your Project
            </Link>
            <Link to="/portfolio" className="btn-outline text-lg px-8 py-4">
              View More Projects
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};